import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { rupees } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHead, Loading, Pill, BrandDot } from "@/components/common";
import { Download } from "lucide-react";

const REPORTS = [
  { id: "registration", name: "Registration" },
  { id: "points_issued", name: "Points Issued" },
  { id: "behaviour", name: "Customer Behaviour" },
  { id: "fund_transfer", name: "Fund Transfer" },
  { id: "upi_transfer", name: "UPI Transfer" },
  { id: "gaming", name: "Gaming" },
  { id: "feedback", name: "Feedback" },
  { id: "scheme", name: "Scheme Performance" },
];

const d10 = (v) => (v ? String(v).slice(0, 10) : "—");
const brandsCell = (ids) => (ids?.length ? ids.map((b) => <span key={b} className="mr-2 whitespace-nowrap"><BrandDot id={b} />{b}</span>) : "—");
const ID_COLS = [{ k: "name", h: "Name", r: (v) => v || "—", cls: "font-medium text-slate-800" }, { k: "mobile", h: "Mobile", cls: "font-mono text-xs" }];

const COLS = {
  registration: [...ID_COLS,
    { k: "registration_brand", h: "Registered from brand", r: () => <span className="text-amber-600 text-xs">Pending definition</span>, csv: () => "PENDING_DEFINITION" },
    { k: "brands_joined", h: "Brands joined", r: brandsCell, csv: (v) => (v || []).join("|") },
    { k: "city", h: "City", r: (v) => v || "—" },
    { k: "total_earned", h: "Total earn", num: true, cls: "text-emerald-600 font-semibold" },
    { k: "total_burned", h: "Total burn", num: true, cls: "text-rose-600" },
    { k: "balance", h: "Balance", num: true, cls: "font-bold text-violet-600" },
    { k: "registered_at", h: "Registered", r: d10 }],
  points_issued: [...ID_COLS,
    { k: "brands", h: "Brands", r: brandsCell, csv: (v) => (v || []).join("|") },
    { k: "scan_points", h: "Scan", num: true },
    { k: "scheme_bonus_points", h: "Scheme bonus", num: true },
    { k: "game_points", h: "Game", num: true },
    { k: "adjustment_points", h: "Adjustment", num: true },
    { k: "total_issued", h: "Total issued", num: true, cls: "font-bold text-violet-600" },
    { k: "last_issued_at", h: "Last issued", r: d10 }],
  behaviour: [...ID_COLS,
    { k: "scans", h: "Scans", num: true },
    { k: "points_earned", h: "Points earned", num: true, cls: "text-emerald-600 font-semibold" },
    { k: "games_played", h: "Games", num: true },
    { k: "last_activity", h: "Last activity", r: d10 }],
  fund_transfer: [...ID_COLS,
    { k: "total_transferred_paise", h: "Transferred", num: true, r: rupees, csv: (v) => (v / 100).toFixed(2), cls: "font-semibold" },
    { k: "succeeded", h: "Succeeded", num: true, cls: "text-emerald-600" },
    { k: "processing", h: "Processing", num: true, cls: "text-amber-600" },
    { k: "failed", h: "Failed", num: true, cls: "text-rose-600" },
    { k: "total_requests", h: "Requests", num: true }],
  upi_transfer: [...ID_COLS,
    { k: "brand_id", h: "Brand", r: (v) => <><BrandDot id={v} />{v}</>, csv: (v) => v },
    { k: "amount_paise", h: "Amount", num: true, r: rupees, csv: (v) => (v / 100).toFixed(2) },
    { k: "status", h: "Status", r: (v) => <Pill status={v}>{v}</Pill>, csv: (v) => v },
    { k: "utr", h: "UTR", r: (v) => v || "—", cls: "font-mono text-xs text-slate-500" },
    { k: "created_at", h: "Date", r: d10 }],
  gaming: [...ID_COLS,
    { k: "plays", h: "Plays", num: true }, { k: "wins", h: "Wins", num: true, cls: "text-emerald-600" },
    { k: "no_wins", h: "No-wins", num: true }, { k: "prize_points", h: "Prize pts", num: true, cls: "font-semibold text-violet-600" },
    { k: "last_played_at", h: "Last played", r: d10 }],
  feedback: [...ID_COLS,
    { k: "responses", h: "Responses", num: true }, { k: "average_rating", h: "Avg rating", num: true, r: (v) => v ?? "—" },
    { k: "latest_rating", h: "Latest", num: true }, { k: "latest_comment", h: "Latest comment", r: (v) => v || "—", cls: "text-slate-500 max-w-[260px] truncate" },
    { k: "last_feedback_at", h: "Date", r: d10 }],
  scheme: [...ID_COLS,
    { k: "schemes", h: "Schemes", r: (v) => (v || []).join(", ") || "—", csv: (v) => (v || []).join("|") },
    { k: "bonus_awards", h: "Bonus awards", num: true }, { k: "bonus_points", h: "Bonus pts", num: true, cls: "font-semibold text-violet-600" },
    { k: "last_bonus_at", h: "Last bonus", r: d10 }],
};

const csvCell = (v) => { let s = v == null ? "" : String(v); if (/^[=+\-@\t\r]/.test(s)) s = "'" + s; return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };

function downloadCsv(rtype, cols, rows, brand) {
  const meta = [`# Report: ${rtype}`, `# Brand filter: ${brand}`, `# Exported: ${new Date().toISOString()}`, `# Rows: ${rows.length}`];
  const lines = [cols.map((c) => c.h).join(","), ...rows.map((r) => cols.map((c) => csvCell(c.csv ? c.csv(r[c.k]) : r[c.k])).join(","))];
  const url = URL.createObjectURL(new Blob(["\ufeff" + [...meta, ...lines].join("\n")], { type: "text/csv" }));
  const a = document.createElement("a"); a.href = url; a.download = `report_${rtype}_${brand}.csv`; a.click(); URL.revokeObjectURL(url);
}

const CustomerTable = ({ rtype, rows, brand }) => {
  const cols = COLS[rtype];
  return (
    <div data-testid={`report-${rtype}-table`}>
      <div className="flex items-center justify-between mb-2">
        <span data-testid="report-row-count" className="text-xs text-slate-500">{rows.length} customer-wise rows</span>
        <button data-testid="report-export-csv" disabled={!rows.length} onClick={() => downloadCsv(rtype, cols, rows, brand)} className="flex items-center gap-1.5 text-xs font-semibold text-violet-700 bg-violet-50 border border-violet-100 px-3 py-1.5 rounded-lg hover:bg-violet-100 disabled:opacity-40"><Download size={13} /> Export CSV</button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-slate-400 text-xs uppercase"><tr>{cols.map((c) => <th key={c.k} className={`py-2 px-2 whitespace-nowrap ${c.num ? "text-right" : "text-left"}`}>{c.h}</th>)}</tr></thead>
          <tbody>{rows.map((r, i) => (
            <tr key={r.payout_id || r.customer_id || i} data-testid={`report-row-${i}`} className="border-t border-slate-100">
              {cols.map((c) => <td key={c.k} className={`py-2 px-2 ${c.num ? "text-right" : ""} ${c.cls || ""}`}>{c.r ? c.r(r[c.k], r) : r[c.k] ?? "—"}</td>)}
            </tr>
          ))}</tbody>
        </table>
      </div>
      {rows.length === 0 && <p className="text-sm text-slate-400 py-6 text-center">No customer records in scope.</p>}
    </div>
  );
};

const Stat = ({ v, l, accent }) => <div><div className={`text-3xl font-bold font-head ${accent ? "text-violet-600" : ""}`}>{v}</div><div className="text-xs text-slate-400">{l}</div></div>;

const Summary = ({ rtype, data }) => {
  const s = {
    registration: [[data.group_customers, "Group customers", 1], [data.consented, "Consented"]],
    points_issued: [[data.total_points_issued?.toLocaleString("en-IN"), "Points issued", 1], [data.customers_count, "Customers"], [data.credit_entries, "Credit entries"]],
    behaviour: [[data.valid_scans, "Valid scans", 1], [data.points_earned, "Points earned"]],
    gaming: [[data.plays, "Plays", 1], [data.wins, "Wins"], [data.no_wins, "No-wins"], [data.prize_points, "Prize points"]],
    feedback: [[`${data.average} / 5`, `${data.responses} responses`, 1]],
  }[rtype];
  return (
    <div className="mb-4">
      {s && <div className="flex gap-8 mb-3">{s.map(([v, l, a]) => <Stat key={l} v={v} l={l} accent={a} />)}</div>}
      {rtype === "feedback" && <div className="flex gap-4 text-sm text-slate-600 mb-2">{[5, 4, 3, 2, 1].map((r) => <span key={r}>{r}★ {data.distribution[r]}</span>)}</div>}
      {rtype === "scheme" && <div className="flex flex-wrap gap-3 mb-2">{data.schemes.map((x, i) => <div key={i} className="bg-slate-50 rounded-lg px-3 py-2 text-sm"><span className="font-semibold">{x.name}</span> · {x.used_budget_points} / {x.total_budget_points} pts</div>)}</div>}
      {data.note && <p className="text-xs text-slate-400">{data.note}</p>}
    </div>
  );
};

export default function Reports() {
  const { selectedBrand } = useApp();
  const [params, setParams] = useSearchParams();
  const tabParam = params.get("tab");
  const [active, setActive] = useState(REPORTS.some((r) => r.id === tabParam) ? tabParam : "registration");
  const [data, setData] = useState(null);

  useEffect(() => {
    if (tabParam && REPORTS.some((r) => r.id === tabParam) && tabParam !== active) setActive(tabParam);
  }, [tabParam]);

  const selectTab = (id) => { setActive(id); setParams({ tab: id }); };

  useEffect(() => {
    setData(null);
    const b = selectedBrand === "ALL" ? "" : `?brand=${selectedBrand}`;
    api.get(`/reports/${active}${b}`).then((r) => setData({ ...r.data, _type: active }));
  }, [active, selectedBrand]);

  return (
    <div>
      <PageHead overline="Analytics & Reports" title="Reports" desc="Built from stored operational records. Brand filter applies to every report. Every report lists customer-wise detail." />
      <div className="flex flex-wrap gap-2 mb-6">
        {REPORTS.map((r) => (
          <button key={r.id} data-testid={`report-tab-${r.id}`} onClick={() => selectTab(r.id)}
            className={`px-3.5 py-2 rounded-lg text-sm font-semibold transition-colors ${active === r.id ? "tnp-gradient text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-violet-300"}`}>{r.name}</button>
        ))}
      </div>
      {!data || data._type !== active ? <Loading /> : (
        <div data-testid={`report-${active.replace("_", "-")}`} className="bg-white rounded-xl border border-slate-200 p-6">
          <Summary rtype={active} data={data} />
          <CustomerTable rtype={active} rows={(active === "upi_transfer" ? data.rows : data.customers) || []} brand={selectedBrand} />
        </div>
      )}
      <div className="text-xs text-slate-400 mt-4">CSV downloads include export timestamp & brand filter · Powered by TagnPay</div>
    </div>
  );
}
